import type { Clock } from "../adapters/clock-port";

export interface CountdownUnit {
  label: string;
  perUnitSeconds: number;
  padTo?: number;
}

export interface CountdownOptions {
  testIdPrefix?: string;
  variantClass?: string;
  onTick?: (seconds: number) => void;
}

export interface CountdownHandle {
  stop(): void;
}

interface RenderedSegment {
  label: string;
  display: string;
}

export function startCountdown(
  container: HTMLElement,
  deadline: Date,
  clock: Clock,
  units: CountdownUnit[],
  options: CountdownOptions = {},
): CountdownHandle {
  return startTickingDisplay(
    container,
    () =>
      Math.max(0, Math.floor((deadline.getTime() - clock.now().getTime()) / 1000)),
    units,
    options,
  );
}

export function startElapsedCounter(
  container: HTMLElement,
  startTime: Date,
  clock: Clock,
  units: CountdownUnit[],
  options: CountdownOptions = {},
): CountdownHandle {
  return startTickingDisplay(
    container,
    () =>
      Math.max(0, Math.floor((clock.now().getTime() - startTime.getTime()) / 1000)),
    units,
    options,
  );
}

function startTickingDisplay(
  container: HTMLElement,
  getDisplaySeconds: () => number,
  units: CountdownUnit[],
  options: CountdownOptions,
): CountdownHandle {
  const testIdPrefix = options.testIdPrefix ?? "countdown";
  const variantClass = options.variantClass ?? "";
  let intervalId: ReturnType<typeof setInterval> | null = null;

  function tick(): void {
    if (!container.isConnected) {
      if (intervalId !== null) clearInterval(intervalId);
      return;
    }
    const seconds = getDisplaySeconds();
    const segments = breakDown(seconds, units);
    container.innerHTML = renderCountdownHtml(segments, testIdPrefix, variantClass);
    options.onTick?.(seconds);
  }

  tick();
  intervalId = setInterval(tick, 1000);

  return {
    stop(): void {
      if (intervalId !== null) clearInterval(intervalId);
    },
  };
}

function breakDown(totalSeconds: number, units: CountdownUnit[]): RenderedSegment[] {
  let remaining = Math.max(0, totalSeconds);
  return units.map((unit) => {
    const value = Math.floor(remaining / unit.perUnitSeconds);
    remaining -= value * unit.perUnitSeconds;
    const padTo = unit.padTo ?? 0;
    const display = padTo > 0 ? value.toString().padStart(padTo, "0") : value.toString();
    return { label: unit.label, display };
  });
}

function renderCountdownHtml(
  segments: RenderedSegment[],
  testIdPrefix: string,
  variantClass: string,
): string {
  const variantSuffix = variantClass ? ` ${variantClass}` : "";
  const segmentsHtml = segments
    .map(
      (seg) => `
      <div class="countdown-segment">
        <span class="countdown-value" data-testid="${testIdPrefix}-${seg.label}">${seg.display}</span>
        <span class="countdown-unit">${seg.label}</span>
      </div>
    `,
    )
    .join("");
  return `
    <div class="countdown${variantSuffix}" data-testid="${testIdPrefix}" aria-live="off">
      ${segmentsHtml}
    </div>
  `;
}
